import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { FC, useEffect, useMemo, useState } from 'react';
import tw from 'twin.macro';
import {
  ButtonPrimary,
  ButtonSecondary,
} from '../../../libs/design-editor/src/common/button/Button';
import {
  useEditor,
  useSelectedLayers,
} from '../../../libs/design-editor/src/hooks';
import { useTranslation } from 'react-i18next';
import { RiQuestionMark } from '@remixicon/react';

interface DiscardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DiscardModal: FC<DiscardModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();

  return (
    <Transition appear show={isOpen}>
      <Dialog
        as="div"
        className="relative z-10 focus:outline-none"
        onClose={onClose}
      >
        <div className="fixed inset-0 bg-black/60 dark:bg-[#282B2B]/80" aria-hidden="true" />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center w-full">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 transform-[scale(95%)]"
              enterTo="opacity-100 transform-[scale(100%)]"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 transform-[scale(100%)]"
              leaveTo="opacity-0 transform-[scale(95%)]"
            >
              <DialogPanel
                css={{
                  borderRadius: 20,
                  width: '100%',
                  maxWidth: 310,
                  ...tw`backdrop-blur-2xl bg-white  dark:bg-black relative`,
                  margin: 'auto 20px',
                  '@media (min-width: 768px)': {
                    maxWidth: 400,
                  },
                }}
              >
                <div
                  css={(theme) => ({
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: '#ef4444',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 32,
                    transform: 'translateX(-50%)',
                    marginTop: -40,
                  })}
                >
                  <RiQuestionMark />
                </div>

                <DialogTitle
                  as="div"
                  css={(theme) => ({
                    paddingTop: 32,
                    paddingLeft: 20,
                    paddingRight: 20,
                    textAlign: 'center',
                    marginBottom: 20,
                    marginTop: 20,
                  })}
                >
                  <h3
                    css={{
                      fontSize: 20,
                      fontWeight: 'bold',
                      textAlign: 'center',
                      ...tw`leading-normal`,
                      '@media (min-width: 768px)': {
                        fontSize: 22,
                        maxWidth: 280,
                        margin: 'auto',
                      },
                    }}
                  >
                    {t('page_campaign.discard.discard_edit')}
                  </h3>
                </DialogTitle>

                <div
                  css={(theme) => ({
                    textAlign: 'center',
                    fontSize: 16,
                    padding: '0px 20px',
                  })}
                >
                  {t('page_campaign.discard.go_back')}
                </div>

                <div
                  css={(theme) => ({
                    marginTop: 20,
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: '0px 20px',
                    paddingBottom: 20,
                    gap: 8,
                  })}
                >
                  <ButtonPrimary
                    css={(theme) => ({
                      width: '100%',
                      textAlign: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '16px 24px',
                      height: 52,
                      borderRadius: 100,
                    })}
                    onClick={onClose}
                  >
                    <span>{t('page_campaign.discard.keep_editing')}</span>
                  </ButtonPrimary>

                  <ButtonSecondary
                    css={(theme) => ({
                      width: '100%',
                      textAlign: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '16px 24px',
                      height: 52,
                      borderRadius: 100,
                      borderColor: 'transparent',
                      ...tw`text-red-500`,
                    })}
                    onClick={onConfirm}
                  >
                    <span>{t('page_campaign.discard.discard')}</span>
                  </ButtonSecondary>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DiscardModal;
